import React, { useEffect } from 'react'
import { Link } from 'gatsby'
import { connectHits } from 'react-instantsearch-dom'
import { Box, SxProps, Typography, useTheme } from '@mui/material'

import { Icon } from './Icon'

import { rudderslabTrackOnClick } from '~/utils/common'

const { DateTime } = require('luxon')

type SingleHitProps = {
  title: string
  slug: string
  blog_category?: string
  blogimage?: { src: string }
  blog_authors?: {
    author_image: { asset: { url: string } }
    author_name: string
  }[]
  event_type?: string
  logoimage?: string
  youtube?: string
  duration?: string
  webinar_dttm?: number
}

type HitsProps = {
  hits: SingleHitProps[]
  type?: string
  showReadNow?: boolean
  onRefineHitsCountChange?: (count: number) => void
  sx?: SxProps
}

// This component renders the results fetched from Algolia index in 3 results in a row format.
// "type" prop helps in determining some parts of the component like main image, category, author (or duration), etc.
export const Hits = ({
  hits,
  type,
  showReadNow,
  onRefineHitsCountChange,
  sx
}: HitsProps) => {
  const { palette, transitions } = useTheme()

  // Updates the hits count in parent component
  useEffect(() => {
    if (typeof onRefineHitsCountChange !== 'undefined') {
      onRefineHitsCountChange(hits.length)
    }
  }, [hits.length, onRefineHitsCountChange])

  return (
    <Box
      data-test='cards'
      sx={{
        columnGap: { xs: 3, tablet: 4 },
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(1, 1fr)',
          mobile: 'repeat(2, 1fr)',
          laptop: 'repeat(3, 1fr)'
        },
        mt: { xs: 3, tablet: 4.5 },
        rowGap: { xs: 3, tablet: 4, laptop: 7 },
        ...sx
      }}>
      {hits.map(hit => {
        const image = type === 'blog' ? hit.blogimage?.src : hit.logoimage
        const category = type === 'blog' ? hit.blog_category : hit.event_type
        const authors = hit.blog_authors?.map(x => x.author_name).join(', ')
        let eventDate
        if (!hit.duration && !!hit.webinar_dttm) {
          eventDate = DateTime.fromJSDate(
            new Date(hit.webinar_dttm * 1000)
          ).toLocaleString({
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          })
        }

        return (
          <Box
            key={hit.title}
            component={Link}
            to={`/${type}/${hit.slug}`}
            onClick={e => rudderslabTrackOnClick('link', hit?.title, e)}
            sx={{
              cursor: 'pointer',
              backgroundColor: 'background.default',
              borderRadius: 6,
              boxShadow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              textDecoration: 'none',
              transition: transitions.create(['all'], {
                duration: transitions.duration.standard
              }),
              '&:hover': {
                transform: `translateY(-1px)`,
                boxShadow: 2
              }
            }}>
            <Box
              component='img'
              src={`${image}?auto=format`}
              sx={{
                width: '100%',
                height: { xs: 190, laptop: 220 },
                objectFit: 'cover'
              }}
              loading='eager'
            />

            <Box
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                p: 3
              }}>
              <Typography
                data-test='event-type'
                variant='subtitle2'
                sx={{ color: 'primary.main', fontWeight: 700 }}>
                {category?.toUpperCase()}
              </Typography>

              <Typography
                variant='h2'
                sx={{
                  color: 'text.primary',
                  flex: 1,
                  fontSize: { xs: 18, laptop: 22 },
                  lineHeight: 1.5,
                  mt: type === 'customers' ? 0 : 1,
                  mb: type === 'customers' ? 1 : 0
                }}>
                {hit.title}
              </Typography>

              {type === 'blog' && (
                <Box
                  sx={{
                    width: '100%',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 4
                  }}>
                  <Typography
                    variant='subtitle2'
                    sx={{
                      color: 'text.primary',
                      fontWeight: 700
                    }}>
                    By {authors}
                  </Typography>
                  {hit?.blog_authors && (
                    <Box
                      component='img'
                      src={hit.blog_authors[0]?.author_image?.asset?.url}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '100%',
                        filter: 'grayscale(1)'
                      }}
                    />
                  )}
                </Box>
              )}

              {type === 'events' && (
                <Box
                  sx={{
                    width: '100%',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 4
                  }}>
                  <Box sx={{ alignItems: 'center', display: 'flex' }}>
                    <Box
                      component='img'
                      src='/images/arrow-right-black.svg'
                      alt='arrow'
                    />
                    <Typography
                      data-test='button-link'
                      variant='subtitle2'
                      sx={{ fontWeight: 700, ml: 1.5 }}>
                      {hit.youtube ? 'Watch Now' : 'Register Now'}
                    </Typography>
                  </Box>
                  {!!hit.duration && (
                    <Box sx={{ alignItems: 'center', display: 'flex' }}>
                      <Box
                        component={Icon}
                        icon='clock'
                        color={palette.scrollBar.main}
                      />
                      <Typography
                        sx={{
                          color: 'scrollBar.main',
                          fontWeight: 700,
                          ml: 1
                        }}>
                        {hit.duration}
                      </Typography>
                    </Box>
                  )}
                  {eventDate && (
                    <Box sx={{ alignItems: 'center', display: 'flex' }}>
                      <Box
                        component={Icon}
                        icon='calendar'
                        color={palette.scrollBar.main}
                      />
                      <Typography
                        sx={{
                          color: 'scrollBar.main',
                          fontWeight: 700,
                          ml: 1
                        }}>
                        {eventDate}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {type === 'customers' && showReadNow && (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    mt: 1
                  }}>
                  <Typography sx={{ color: 'text.primary', fontSize: 14 }}>
                    Read Now
                  </Typography>

                  <Box
                    component={Icon}
                    icon='arrow-right'
                    color='text.primary'
                    sx={{ ml: 2 }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export const CustomHitsThreeRow = connectHits(Hits)
